/**
 * useLocalStorage Hook
 *
 * A React hook for persisting state to localStorage with automatic
 * serialization and synchronization across tabs.
 */

import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing state that persists to localStorage
 *
 * @param key - The localStorage key to store the value under
 * @param initialValue - The initial value if no stored value exists
 * @returns A tuple of [value, setValue] similar to useState
 *
 * @example
 * ```typescript
 * // Store a simple value
 * const [theme, setTheme] = useLocalStorage('theme', 'light');
 *
 * // Store an object
 * const [filters, setFilters] = useLocalStorage<TransactionFilters>(
 *   'transactionFilters',
 *   { dateFrom: null, dateTo: null, accountId: null, classificationStatus: [], owner: null, search: null }
 * );
 *
 * // Update with a callback
 * setFilters(prev => ({ ...prev, owner: 'family' }));
 * ```
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize state with stored value or initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Memoized setter that updates both state and localStorage
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Allow value to be a function for functional updates
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));

          // Dispatch a custom event so other tabs/windows can sync
          window.dispatchEvent(
            new StorageEvent('storage', {
              key,
              newValue: JSON.stringify(valueToStore),
            })
          );
        }
      } catch (error) {
        console.error(`Error saving to localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Listen for changes from other tabs/windows
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) {
        try {
          setStoredValue(JSON.parse(event.newValue) as T);
        } catch (error) {
          console.warn(`Error parsing storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
}

/**
 * Remove a value from localStorage
 *
 * @param key - The localStorage key to remove
 */
export function removeFromLocalStorage(key: string): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(key);
  }
}

/**
 * Clear all values from localStorage
 * Use with caution - this removes ALL stored data
 */
export function clearLocalStorage(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.clear();
  }
}

/**
 * Check if a key exists in localStorage
 *
 * @param key - The localStorage key to check
 * @returns True if the key exists
 */
export function hasLocalStorageKey(key: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(key) !== null;
}

/**
 * Get a value from localStorage without subscribing to updates
 *
 * @param key - The localStorage key to read
 * @param defaultValue - The default value if the key doesn't exist
 * @returns The stored value or the default
 */
export function getLocalStorageValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.warn(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
}

// =============================================================================
// Accounting-Specific Storage Keys
// =============================================================================

/**
 * Predefined localStorage keys for accounting features
 * Using constants helps prevent typos and enables autocomplete
 */
export const ACCOUNTING_STORAGE_KEYS = {
  /** Saved transaction filters */
  TRANSACTION_FILTERS: 'accounting:transactionFilters',
  /** User's preferred page size for transaction list */
  PAGE_SIZE: 'accounting:pageSize',
  /** Last selected account in the account picker */
  LAST_ACCOUNT: 'accounting:lastAccount',
  /** Collapsed state for account tree nodes */
  COLLAPSED_ACCOUNTS: 'accounting:collapsedAccounts',
  /** User's preferred column visibility */
  VISIBLE_COLUMNS: 'accounting:visibleColumns',
  /** Recently used accounts for quick access */
  RECENT_ACCOUNTS: 'accounting:recentAccounts',
} as const;

export type AccountingStorageKey = (typeof ACCOUNTING_STORAGE_KEYS)[keyof typeof ACCOUNTING_STORAGE_KEYS];
